import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "field" }
const _hoisted_2 = ["for"]
const _hoisted_3 = { class: "control" }

import { ref, computed } from "vue";
import VueSelect, { type Option } from "vue3-select-component";
import { debounce, snakeCase } from "lodash-es";

interface Props {
  label?: string;
  modelValue: Option<number>[];
  searchPathIdentifier:
    | "games"
    | "series"
    | "platforms"
    | "genres"
    | "engines"
    | "companies"
    | "stores";
  placeholder?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'multi-select',
  props: {
    label: {},
    modelValue: {},
    searchPathIdentifier: {},
    placeholder: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Reactive data
// Store search results separately from pre-selected values
const searchOptions = ref<Option<number>[]>([]);
const searchPath = computed(
  () => `${window.location.origin}/${props.searchPathIdentifier}/search.json`
);
const isLoading = ref(false);

// Combine pre-selected values with search results for the options list
// This ensures selected values can always be looked up by the component
const allOptions = computed(() => {
  const selected = props.modelValue;
  const searched = searchOptions.value;
  // Merge, avoiding duplicates by value
  const merged = [...selected];
  for (const opt of searched) {
    if (!merged.some((s) => s.value === opt.value)) {
      merged.push(opt);
    }
  }
  return merged;
});

// Extract just the values for modelValue (vue3-select-component expects values, not Options)
const selectedValues = computed(() => props.modelValue.map((opt) => opt.value));

const defaultOptionFunc = (item: { id: number; name: string }): Option<number> => ({
  label: item.name,
  value: item.id
});

// Methods
// When values change, look up full Option objects and emit
function handleValueChange(values: number | number[] | null | undefined) {
  if (values == null) {
    emit("update:modelValue", []);
    return;
  }
  // vue3-select-component emits array for isMulti
  const valuesArray = Array.isArray(values) ? values : [values];
  const selectedOptions = valuesArray
    .map((v) => allOptions.value.find((opt) => opt.value === v))
    .filter((opt): opt is Option<number> => opt !== undefined);
  emit("update:modelValue", selectedOptions);
}

/*
 * @param {search}  String   Current search text
 */
const onSearch = debounce(async (search: string) => {
  isLoading.value = true;
  const searchUrl = new URL(searchPath.value);
  searchUrl.searchParams.append("query", search);
  try {
    const response = await fetch(searchUrl.toString(), {
      headers: {
        "Content-Type": "application/json"
      }
    });

    const data: { id: number; name: string }[] = await response.json();
    searchOptions.value = data.map(defaultOptionFunc);
  } catch (e) {
    console.error(`Error searching ${props.searchPathIdentifier}:`, e);
  } finally {
    isLoading.value = false;
  }
}, 250);

// Computed properties
const inputId = computed(() => {
  return snakeCase(props.label);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "label",
          for: inputId.value
        }, _toDisplayString(__props.label), 9, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(VueSelect), {
        isMulti: true,
        options: allOptions.value as any,
        onSearch: _unref(onSearch),
        inputId: __props.label ? inputId.value : undefined,
        placeholder: __props.placeholder,
        isLoading: isLoading.value,
        modelValue: selectedValues.value,
        "onUpdate:modelValue": handleValueChange
      }, null, 8, ["options", "onSearch", "inputId", "placeholder", "isLoading", "modelValue"])
    ])
  ]))
}
}

})